import React from 'react'
import { Dialog } from '@headlessui/react'
import { currencySymbolMapping } from '../../../../../CommonComponent/staticValues'

export default function AuctionRulesModal({ auctionData, isOpen, setIsOpen }) {
    const currencySymbol = currencySymbolMapping[auctionData?.currency] || '$';

    const isVoluntaryContribution = auctionData?.add_buyer_fees === 'voluntary';
    const isPercentageFee = auctionData?.add_buyer_fees === 'percentage';
    const isFixedFee = auctionData?.add_buyer_fees === 'fixed';

    const voluntaryFeeMsg = "This auction has optional buyer fees. " +
        "At checkout, you may remove this if you prefer not to contribute.";
    const percentageFeeMsg = `This auction has a buyer's fee of ${auctionData?.percentage || 0}%, ` +
        `which is added onto the sale of each lot. `;
    const fixedFeeMsg = `This auction has a fixed buyer's fee of ${currencySymbol}${auctionData?.fees || 0} per lot, ` +
        `which is added onto the total when you check out. `;

    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)}
            data-te-modal-init
            className="fixed left-0 top-0 z-[1055] h-full w-full overflow-y-auto overflow-x-hidden outline-none bg-[#282828cc]"
            id="auction_rules_dialog"
            tabIndex="-1"
            aria-labelledby="auction_rules_dialog_label"
            aria-modal="true"
            role="dialog"
        >
            <Dialog.Panel
                data-te-modal-dialog-ref
                className="pointer-events-none relative flex min-h-[calc(100%-1rem)] mx-5 w-auto
                items-center transition-all duration-300 ease-in-out min-[740px]:mx-auto
                min-[740px]:min-h-[calc(100%-3.5rem)] min-[740px]:max-w-[658px]"
            >
                <div className="pointer-events-auto relative flex w-full flex-col rounded-md border-none bg-white 
                    bg-clip-padding text-current shadow-lg outline-none"
                >
                    <Dialog.Title
                        className="flex flex-shrink-0 border-b border-[#A1A1A9] items-center justify-between rounded-t-md p-4">
                        <p data-testid='auctionRules' 
                            className="text-[22px] text-[#343434] leading-normal pl-3 font-medium">
                            Auction Rules
                        </p>
                        {/* <!--Close button--> */}
                        <button onClick={() => {setIsOpen(false)}}
                            type="button"
                            data-testid="close_rules"
                            className="box-content rounded-none border-none"
                            data-te-modal-dismiss
                            aria-label="Close">
                            <img src="/images/CloseButton-modalcontent.svg" alt="img" />
                        </button>
                    </Dialog.Title>
                    {/* <!--Modal body--> */}
                    <div className="relative p-4 sm:mx-8 mx-2.5 h-[330px] mt-2 mb-5 
                        overflow-y-auto overflow-x-hidden customScrollbar text-[14px]
                        text-[#343434] leading-normal">
                        <p className='mb-4 font-sans' style={{fontWeight: '700'}} >
                            Auction Ending
                        </p>
                        <p className='mb-4'>
                            This is a Timed Auction with {auctionData?.extension_type || 'Unknown'} ending.<br/>
                            {auctionData?.extension_type === 'All Lots' && (
                                'Bidding closes on all lots at the same time.'
                            )}

                            {auctionData?.extension_type === 'Cascade' && (
                                'The lots close in consecutive order and only begin closing after the previous lot has ended.'
                            )}

                            {auctionData?.extension_type === 'Individual Lots' && (
                                'Each lot has its own closing time and is not affected by bids on any other lots in the auction.'
                            )}
                        </p>
                        <p className='mb-4 font-sans' style={{fontWeight: '700'}}>
                            Bidding Extensions
                        </p>
                        <p className='mb-4'>
                            {auctionData?.extension_type === 'All Lots' && (
                                `A bid placed in the last minute of the auction, 
                                extends the bidding on all the lots in the auction by 
                                ${auctionData?.extension_time || 0} minutes.`
                            )}

                            {auctionData?.extension_type === 'Cascade' && (
                                `A bid placed in the last minute of a lot closing, 
                                extends the bidding by ${auctionData?.extension_time || 0} 
                                minutes on this lot and any subsequent lots.`
                            )}

                            {auctionData?.extension_type === 'Individual Lots' && (
                                `Any bid placed in the last minute of a lot closing, 
                                extends the bidding on the individual lot by ${auctionData?.extension_time || 0} minutes. 
                                This may mean that the lots close out of sequential order.`
                            )}
                        </p>
                        <p className='mb-4 font-sans' style={{fontWeight: '700'}} >
                            Buyer Fees
                        </p>
                        <p className='mb-4'>
                            {isVoluntaryContribution
                                ? voluntaryFeeMsg
                                : isPercentageFee
                                    ? percentageFeeMsg
                                    : isFixedFee
                                        ? fixedFeeMsg
                                        : "There is no buyer's fee for this auction."
                            }
                        </p>
                        <p className='mb-4 font-sans' style={{fontWeight: '700'}}>
                            Currency
                        </p>
                        <p className='mb-4'>
                            This auction is in {auctionData?.currency || 'USD'} ({currencySymbol})
                        </p>
                        <p className='mb-4 font-sans' style={{fontWeight: '700'}}>
                            Registration Requirements
                        </p>
                        <p className='mb-4'>
                            {auctionData?.registration_type === 'Email only' && (
                                `This auction has no registration requirements.
                                You can register with just your email address and start bidding in the auction.`
                            )}
                            {auctionData?.registration_type === 'Credit (bank) card validation' && (
                                `A credit (bank) card validation is required in order to register for this auction.
                                You will not be able to bid in the auction until you have validated your credit card.`
                            )}
                            {auctionData?.registration_type === 'Manual approval' && (
                                `Registration for this auction is by manual approval.
                                You will receive a paddle number only once the seller has approved your registration request. `
                            )}
                            {auctionData?.registration_type === 'Register with credit card' && (
                                `A valid credit or debit card is required to register for this auction. 
                                Kindly note that your card will be charged automatically ` +
                                `for the total amount due if you are the winning bidder.`
                            )}
                        </p>
                        <p className='mb-4 font-sans' style={{fontWeight: '700'}}>
                            Auction Results
                        </p>
                        <p className='mb-4'>
                            {auctionData?.publish_auction_results ? (
                                `The results for this auction will be public.
                                Everyone will be able to see the final price of the lots in the auction.`
                            ) : (
                                `The results for this auction are hidden. 
                                You will not see the final price of lots unless you are the winning bidder.`
                            )}
                        </p>
                    </div>
                </div>
            </Dialog.Panel>
        </Dialog>
    )
}
